/**
 * A chosen song (P5.5): its meaning and sections (step 2), then what comes
 * after practice (steps 4–6) — pronunciation/rhythm UNAVAILABLE, the challenge
 * GATED, sharing only through per-item publication and only with
 * recording_share. Every rights-bearing field goes through utils/songRights.
 */
import { Link } from 'react-router-dom';
import type { SongSectionView, SongView } from '../../store/api/songLearningApi';
import { SongSectionPanel } from './SongSectionPanel';
import { practiceLanguageOption } from '../../config/practiceLanguages';
import {
  AUDIO_UNAVAILABLE,
  CHALLENGE_GATED,
  PRONUNCIATION_UNAVAILABLE,
  RIGHTS_BASIS_LABEL,
  SHARE_ALLOWED,
  SHARE_NOT_PERMITTED,
  granted,
  shareStatus,
} from '../../utils/songRights';

export const languageLabel = (code: string) => practiceLanguageOption(code)?.label ?? code;

/** Steps 4–6 for a chosen song: what is unavailable, gated or permitted, and why. */
export function SongAfterPractice({ view, teamChallengesOn }: { view: SongView; teamChallengesOn: boolean }) {
  const share = shareStatus(view);
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <div className="rounded-lg border border-gray-200 p-4" data-testid="song-pronunciation" data-status="UNAVAILABLE">
        <h3 className="text-sm font-medium text-gray-900">Pronunciation &amp; rhythm</h3>
        <p className="mt-1 text-xs text-gray-600">{PRONUNCIATION_UNAVAILABLE}</p>
      </div>
      <div className="rounded-lg border border-gray-200 p-4" data-testid="song-challenge" data-status="GATED">
        <h3 className="text-sm font-medium text-gray-900">Challenge a teammate</h3>
        <p className="mt-1 text-xs text-gray-600">{CHALLENGE_GATED}</p>
        {teamChallengesOn && (
          <Link to="/challenges" className="mt-2 inline-block text-xs text-primary-600 hover:underline">
            See team challenges
          </Link>
        )}
      </div>
      <div className="rounded-lg border border-gray-200 p-4" data-testid="song-share" data-status={share}>
        <h3 className="text-sm font-medium text-gray-900">Share</h3>
        <p className="mt-1 text-xs text-gray-600">{share === 'allowed' ? SHARE_ALLOWED : SHARE_NOT_PERMITTED}</p>
        {share === 'allowed' && (
          <Link to="/portfolio" className="mt-2 inline-block text-xs text-primary-600 hover:underline">
            Open Portfolio
          </Link>
        )}
      </div>
    </div>
  );
}

/** Step 2: the chosen song's meaning and sections. */
export function SongDetail({
  view,
  onSpeakAlong,
  teamChallengesOn,
}: {
  view: SongView;
  onSpeakAlong?: (section: SongSectionView) => void;
  teamChallengesOn: boolean;
}) {
  return (
    <div className="space-y-6" data-testid="song-detail">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">{view.title}</h2>
        <p className="mt-1 text-sm text-gray-600">{view.about}</p>
        <p className="mt-2 text-xs text-gray-500">
          {languageLabel(view.language)} · {view.level} · {RIGHTS_BASIS_LABEL[view.rightsBasis] ?? view.rightsBasis}
        </p>
        <p className="mt-1 text-xs text-gray-500" data-testid="song-provenance">{view.provenance}</p>
        {!granted(view, 'audio_playback') && <p className="mt-1 text-xs text-gray-500">{AUDIO_UNAVAILABLE}</p>}
      </div>

      <div className="space-y-3">
        {view.sections.map((s) => (
          <SongSectionPanel key={s.id} view={view} section={s} onSpeakAlong={onSpeakAlong} />
        ))}
      </div>

      <SongAfterPractice view={view} teamChallengesOn={teamChallengesOn} />
    </div>
  );
}
